import { useEffect, useRef } from 'react'
import Phaser from 'phaser'
import type { GameMode, MarketTick, RaidState, RoundState } from '../shared/contracts'

interface OceanStageProps {
  mode: GameMode | 'lobby'
  tick?: MarketTick
  history?: MarketTick[]
  round?: RoundState
  raid?: RaidState
  reducedMotion: boolean
  frenzy: boolean
}

type VisualState = OceanStageProps

// Centralized so the rider keeps the same visual size after a viewport resize.
const FISH_SCALE_PORTRAIT = 0.066
const FISH_SCALE_LANDSCAPE = 0.096

class AbyssScene extends Phaser.Scene {
  private backdrop!: Phaser.GameObjects.Image
  private fish!: Phaser.GameObjects.Image
  private whale!: Phaser.GameObjects.Image
  private current!: Phaser.GameObjects.Graphics
  private sonar!: Phaser.GameObjects.Graphics
  private motes?: Phaser.GameObjects.Particles.ParticleEmitter
  private ticks: MarketTick[] = []
  private visual: VisualState = { mode: 'lobby', reducedMotion: false, frenzy: false }

  constructor() {
    super('abyss')
  }

  preload(): void {
    this.load.image('abyss-desktop', '/assets/ocean-abyss-desktop.webp')
    this.load.image('abyss-mobile', '/assets/ocean-abyss-mobile.webp')
    this.load.image('rider', '/assets/current-rider-fish.png')
    this.load.image('whale', '/assets/whale-boss.png')
  }

  create(): void {
    const portrait = this.scale.width < 700
    this.backdrop = this.add.image(0, 0, portrait ? 'abyss-mobile' : 'abyss-desktop').setOrigin(0.5)
    this.current = this.add.graphics()
    this.sonar = this.add.graphics()
    this.fish = this.add.image(this.scale.width * 0.55, this.scale.height * 0.48, 'rider').setScale(portrait ? FISH_SCALE_PORTRAIT : FISH_SCALE_LANDSCAPE)
    this.whale = this.add.image(this.scale.width * 0.72, this.scale.height * 0.5, 'whale').setScale(portrait ? 0.24 : 0.42).setAlpha(0)

    const particle = this.add.graphics().fillStyle(0x70e8dc, 1).fillCircle(3, 3, 3)
    particle.generateTexture('mote', 6, 6)
    particle.destroy()
    this.motes = this.add.particles(0, 0, 'mote', {
      x: { min: 0, max: this.scale.width },
      y: { min: 0, max: this.scale.height },
      lifespan: { min: 2400, max: 5000 },
      speedX: { min: -8, max: 14 },
      speedY: { min: -18, max: -5 },
      alpha: { start: 0, end: 0.35 },
      scale: { start: 0.3, end: 1.1 },
      frequency: 180,
      blendMode: Phaser.BlendModes.ADD,
    })
    this.scale.on('resize', () => this.layout())
    this.layout()
  }

  setVisual(next: VisualState): void {
    if (next.history?.length) {
      const merged = new Map(this.ticks.map((tick) => [tick.sequence, tick]))
      for (const tick of next.history) merged.set(tick.sequence, tick)
      this.ticks = [...merged.values()].sort((a, b) => a.sourceTimestamp - b.sourceTimestamp).slice(-480)
    }
    if (next.tick && next.tick.sequence !== this.visual.tick?.sequence) {
      this.ticks.push(next.tick)
      this.ticks = this.ticks.slice(-480)
    }
    this.visual = next
    if (this.current) this.draw()
  }

  update(): void {
    this.draw()
  }

  private layout(): void {
    if (!this.backdrop) return
    const { width, height } = this.scale
    const portrait = width < 700
    const source = this.textures.get(portrait ? 'abyss-mobile' : 'abyss-desktop').getSourceImage() as HTMLImageElement
    const cover = Math.max(width / source.width, height / source.height)
    this.backdrop.setTexture(portrait ? 'abyss-mobile' : 'abyss-desktop').setPosition(width / 2, height / 2).setScale(cover)
    this.fish.setScale(portrait ? FISH_SCALE_PORTRAIT : FISH_SCALE_LANDSCAPE)
    this.whale.setScale(portrait ? 0.24 : 0.42)
  }

  private draw(): void {
    if (!this.current) return
    const { width, height } = this.scale
    const portrait = width < 700
    const playableTop = height * 0.2
    const playableHeight = height * 0.62
    const latestTimestamp = this.visual.tick?.sourceTimestamp ?? this.ticks.at(-1)?.sourceTimestamp ?? Date.now()
    const historyWindowMs = 60_000
    const visibleTicks = this.ticks.filter((tick) => tick.sourceTimestamp >= latestTimestamp - historyWindowMs)
    const prices = visibleTicks.map((tick) => tick.price)
    const anchor = this.visual.round?.entryPrice ?? this.visual.tick?.price ?? prices.at(-1) ?? 60_000
    const spread = Math.max(this.visual.round?.bandSize ? this.visual.round.bandSize * 7 : 25, ...prices.map((price) => Math.abs(price - anchor)), 1)
    const classic = this.visual.mode === 'classic-swim'
    const fishX = classic ? width * (portrait ? 0.22 : 0.47) : width * (portrait ? 0.68 : 0.62)
    const historyStartX = width * 0.025
    const historyWidth = Math.max(1, fishX - historyStartX)
    const pointFor = (tick: MarketTick) => new Phaser.Math.Vector2(
      fishX - ((latestTimestamp - tick.sourceTimestamp) / historyWindowMs) * historyWidth,
      playableTop + playableHeight / 2 - ((tick.price - anchor) / spread) * playableHeight * 0.43,
    )
    const points = visibleTicks.map(pointFor)
    if (points.length === 0) points.push(new Phaser.Math.Vector2(fishX, playableTop + playableHeight / 2))
    if (points.length === 1) points.unshift(new Phaser.Math.Vector2(fishX - 18, points[0].y))

    this.current.clear()
    if (!classic) {
      this.current.lineStyle(this.visual.frenzy ? 4 : 2, this.visual.frenzy ? 0xb6ff54 : 0x55dfff, 0.9)
      this.current.beginPath()
      this.current.moveTo(points[0].x, points[0].y)
      for (const point of points.slice(1)) this.current.lineTo(point.x, point.y)
      this.current.strokePath()
    }

    const last = points.at(-1)!
    const bob = this.visual.reducedMotion ? 0 : Math.sin(this.time.now / 420) * 5
    this.fish.setPosition(fishX + (portrait ? 4 : 6), last.y + bob)
    this.fish.setAlpha(classic ? 0 : this.visual.mode === 'whale-raid' ? 0.72 : 1)

    const raidVisible = this.visual.mode === 'whale-raid'
    this.whale.setAlpha(raidVisible ? 0.92 : 0).setPosition(width * 0.72, height * 0.48 + bob * 0.4)
    this.motes?.setFrequency(this.visual.reducedMotion ? -1 : this.visual.frenzy ? 55 : 180)

    this.sonar.clear()
    this.sonar.lineStyle(1, 0x72eadc, 0.18)
    const rings = this.visual.mode === 'classic-swim' ? 5 : raidVisible ? 3 : 2
    for (let index = 1; index <= rings; index += 1) {
      const radius = (Math.min(width, height) * 0.1) * index
      this.sonar.strokeCircle(raidVisible ? width * 0.72 : this.fish.x, raidVisible ? height * 0.48 : this.fish.y, radius)
    }
  }
}

export function OceanStage(props: OceanStageProps) {
  const host = useRef<HTMLDivElement>(null)
  const scene = useRef<AbyssScene | null>(null)

  useEffect(() => {
    if (!host.current) return
    const abyss = new AbyssScene()
    scene.current = abyss
    const game = new Phaser.Game({
      type: Phaser.AUTO,
      parent: host.current,
      transparent: true,
      antialias: true,
      render: { powerPreference: 'high-performance' },
      scale: { mode: Phaser.Scale.RESIZE, width: '100%', height: '100%' },
      scene: abyss,
    })
    return () => {
      scene.current = null
      game.destroy(true)
    }
  }, [])

  useEffect(() => {
    scene.current?.setVisual(props)
  }, [props])

  return <div className="ocean-stage" ref={host} aria-hidden="true" />
}
